import type { AgentRuntimeComputationState } from "@/lib/agent-runtime-sync-batch-computation"
import type { ThreadSyncReplayWorkerEvent } from "@/lib/thread-sync-replay-worker-types"

export type BoundedSerializedSizeEstimate = {
  estimatedBytes: number
  truncated: boolean
}

type SizeEstimateContext = {
  visitedNodeCount: number
  truncated: boolean
  activeObjects: Set<object>
}

/**
 * The estimator only needs enough depth to capture the nested message/content
 * shapes inside runtime snapshots and raw projections. Ten levels is enough to
 * reach nested assistant message content payloads without letting a malformed
 * future payload recurse arbitrarily deep.
 */
const THREAD_SYNC_BOUNDED_SIZE_ESTIMATE_MAX_DEPTH = 10

/**
 * Five thousand visited nodes is enough to classify ordinary replay requests
 * against the worker safety budgets, while keeping the estimator below the
 * long-task threshold on large chat/source snapshots. Larger payloads are
 * deliberately marked truncated so callers fail safe instead of spending tens
 * of milliseconds walking objects on the main thread.
 */
const THREAD_SYNC_BOUNDED_SIZE_ESTIMATE_MAX_VISITED_NODES = 5_000

/**
 * Exact UTF-8 sizing is cheap for small strings, but traces showed large source
 * and tool-output strings dominating estimator time. Four thousand code units
 * keeps exact JavaScript loops comfortably small while large strings use a
 * conservative length-based upper bound without scanning every character.
 */
const THREAD_SYNC_EXACT_STRING_SIZE_MAX_CODE_UNITS = 4_096

/**
 * ASCII is one byte per code unit in UTF-8. Keeping this as a named constant
 * makes the large-string fast path explicit and auditable.
 */
const THREAD_SYNC_ASCII_BYTES_PER_CODE_UNIT = 1

/**
 * 0x7f is the largest single-byte ASCII code unit. The exact small-string loop
 * uses this boundary to avoid the slower multi-byte cases for common text.
 */
const THREAD_SYNC_ASCII_MAX_CODE_UNIT = 0x7f

/**
 * UTF-8 needs at most three bytes for an individual UTF-16 code unit; surrogate
 * pairs use four bytes for two code units, so this upper bound remains safe.
 * We use it only when the large-string regexp scan proves the string is not
 * entirely ASCII.
 */
const THREAD_SYNC_UTF8_MAX_BYTES_PER_CODE_UNIT = 3

/**
 * JSON string quotes add two bytes. Keeping this as a named constant avoids
 * scattering serialization assumptions through the estimator.
 */
const THREAD_SYNC_JSON_STRING_QUOTE_BYTES = 2

/**
 * Large strings use the same upper-bound bytes-per-code-unit estimate as the
 * non-ASCII exact path. This can overestimate large ASCII source/tool output and
 * bypass worker offload earlier, but that is safer than spending input-task CPU
 * scanning strings that are already large enough to create structured-clone
 * pressure.
 */
const THREAD_SYNC_LARGE_STRING_ESTIMATED_BYTES_PER_CODE_UNIT =
  THREAD_SYNC_UTF8_MAX_BYTES_PER_CODE_UNIT

function estimateStringBytes(args: { value: string }): number {
  if (args.value.length > THREAD_SYNC_EXACT_STRING_SIZE_MAX_CODE_UNITS) {
    return (
      args.value.length * THREAD_SYNC_LARGE_STRING_ESTIMATED_BYTES_PER_CODE_UNIT +
      THREAD_SYNC_JSON_STRING_QUOTE_BYTES
    )
  }

  let utf8ByteCount = 0

  for (let index = 0; index < args.value.length; index += 1) {
    const codePoint = args.value.charCodeAt(index)
    if (codePoint <= THREAD_SYNC_ASCII_MAX_CODE_UNIT) {
      utf8ByteCount += THREAD_SYNC_ASCII_BYTES_PER_CODE_UNIT
      continue
    }

    if (codePoint <= 0x7ff) {
      utf8ByteCount += 2
      continue
    }

    if (codePoint >= 0xd800 && codePoint <= 0xdbff && index + 1 < args.value.length) {
      const nextCodePoint = args.value.charCodeAt(index + 1)
      if (nextCodePoint >= 0xdc00 && nextCodePoint <= 0xdfff) {
        utf8ByteCount += 4
        index += 1
        continue
      }
    }

    utf8ByteCount += 3
  }

  return utf8ByteCount + THREAD_SYNC_JSON_STRING_QUOTE_BYTES
}

function estimatePrimitiveBytes(args: {
  value: string | number | boolean | bigint | null | undefined
}): number {
  if (args.value === null) {
    return 4
  }

  if (typeof args.value === "string") {
    return estimateStringBytes({ value: args.value })
  }

  if (typeof args.value === "boolean") {
    return args.value ? 4 : 5
  }

  if (typeof args.value === "number") {
    return Number.isFinite(args.value) ? String(args.value).length : 4
  }

  if (typeof args.value === "bigint") {
    return String(args.value).length + 2
  }

  return 9
}

function markEstimateAsTruncated(args: { context: SizeEstimateContext }): void {
  args.context.truncated = true
}

function estimateArrayBytes(args: {
  values: unknown[]
  depth: number
  context: SizeEstimateContext
}): number {
  let totalBytes = 2
  for (let index = 0; index < args.values.length; index += 1) {
    if (index > 0) {
      totalBytes += 1
    }

    totalBytes += estimateUnknownBytes({
      value: args.values[index],
      depth: args.depth + 1,
      context: args.context,
    })
  }

  return totalBytes
}

function estimateMapBytes(args: {
  value: Map<unknown, unknown>
  depth: number
  context: SizeEstimateContext
}): number {
  if (args.value.size === 0) {
    return 2
  }

  let entryCount = 0
  let totalBytes = 2
  for (const [key, entryValue] of args.value) {
    if (entryCount > 0) {
      totalBytes += 1
    }

    totalBytes += estimateUnknownBytes({
      value: key,
      depth: args.depth + 1,
      context: args.context,
    })
    totalBytes += 1
    totalBytes += estimateUnknownBytes({
      value: entryValue,
      depth: args.depth + 1,
      context: args.context,
    })
    entryCount += 1
  }

  return totalBytes
}

function estimateObjectBytes(args: {
  value: Record<string, unknown>
  depth: number
  context: SizeEstimateContext
}): number {
  let keyCount = 0
  let totalBytes = 2

  for (const key in args.value) {
    if (!Object.prototype.hasOwnProperty.call(args.value, key)) {
      continue
    }

    if (keyCount > 0) {
      totalBytes += 1
    }

    totalBytes += estimateStringBytes({ value: key })
    totalBytes += 1
    totalBytes += estimateUnknownBytes({
      value: args.value[key],
      depth: args.depth + 1,
      context: args.context,
    })
    keyCount += 1
  }

  return totalBytes
}

function estimateUnknownBytes(args: {
  value: unknown
  depth: number
  context: SizeEstimateContext
}): number {
  if (args.context.visitedNodeCount >= THREAD_SYNC_BOUNDED_SIZE_ESTIMATE_MAX_VISITED_NODES) {
    markEstimateAsTruncated({ context: args.context })
    return 0
  }

  args.context.visitedNodeCount += 1

  if (args.depth > THREAD_SYNC_BOUNDED_SIZE_ESTIMATE_MAX_DEPTH) {
    markEstimateAsTruncated({ context: args.context })
    return 0
  }

  if (
    args.value === null
    || typeof args.value === "string"
    || typeof args.value === "number"
    || typeof args.value === "boolean"
    || typeof args.value === "bigint"
    || typeof args.value === "undefined"
  ) {
    return estimatePrimitiveBytes({
      value: args.value as string | number | boolean | bigint | null | undefined,
    })
  }

  if (typeof args.value === "symbol" || typeof args.value === "function") {
    markEstimateAsTruncated({ context: args.context })
    return 0
  }

  if (typeof args.value !== "object") {
    return 0
  }

  if (args.context.activeObjects.has(args.value)) {
    markEstimateAsTruncated({ context: args.context })
    return 0
  }

  args.context.activeObjects.add(args.value)

  try {
    if (Array.isArray(args.value)) {
      return estimateArrayBytes({
        values: args.value,
        depth: args.depth,
        context: args.context,
      })
    }

    if (args.value instanceof Date) {
      return estimateStringBytes({ value: args.value.toISOString() })
    }

    if (args.value instanceof ArrayBuffer) {
      return args.value.byteLength
    }

    if (ArrayBuffer.isView(args.value)) {
      return args.value.byteLength
    }

    if (args.value instanceof Map) {
      return estimateMapBytes({
        value: args.value,
        depth: args.depth,
        context: args.context,
      })
    }

    if (args.value instanceof Set) {
      return estimateArrayBytes({
        values: Array.from(args.value.values()),
        depth: args.depth,
        context: args.context,
      })
    }

    return estimateObjectBytes({
      value: args.value as Record<string, unknown>,
      depth: args.depth,
      context: args.context,
    })
  } finally {
    args.context.activeObjects.delete(args.value)
  }
}

export function estimateBoundedSerializedSize(args: {
  value: unknown
}): BoundedSerializedSizeEstimate {
  const context: SizeEstimateContext = {
    visitedNodeCount: 0,
    truncated: false,
    activeObjects: new Set<object>(),
  }

  return {
    estimatedBytes: estimateUnknownBytes({
      value: args.value,
      depth: 0,
      context,
    }),
    truncated: context.truncated,
  }
}

export function estimateThreadSyncReplayWorkerRequestSize(args: {
  currentState: AgentRuntimeComputationState
  events: ThreadSyncReplayWorkerEvent[]
}): {
  currentStateBytes: number
  chunkEventsBytes: number
  requestBytes: number
  currentStateWasTruncated: boolean
  chunkEventsWereTruncated: boolean
} {
  const currentStateEstimate = estimateBoundedSerializedSize({
    value: args.currentState,
  })
  const chunkEventsEstimate = estimateBoundedSerializedSize({
    value: args.events,
  })

  return {
    currentStateBytes: currentStateEstimate.estimatedBytes,
    chunkEventsBytes: chunkEventsEstimate.estimatedBytes,
    requestBytes: currentStateEstimate.estimatedBytes + chunkEventsEstimate.estimatedBytes,
    currentStateWasTruncated: currentStateEstimate.truncated,
    chunkEventsWereTruncated: chunkEventsEstimate.truncated,
  }
}
